import { Users, UserCheck, CalendarDays, UserX, UserPlus } from 'lucide-react'
import { format } from 'date-fns'
import { StatCard } from '@/components/common'
import { Skeleton } from '@/components/ui/skeleton'
import { useSummary } from '../api'

/**
 * The five headline metrics across the top of the dashboard.
 *
 * Percentages come from the backend rather than being recomputed here: the server already
 * rounds them to one decimal, and doing the arithmetic twice invites the two from
 * disagreeing on a boundary.
 */
export function StatCardsRow({ date }: { date?: string }) {
  const { data, isLoading, isError } = useSummary(date)

  if (isLoading || (!data && !isError)) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-[104px] rounded-[var(--radius-card)]" />
        ))}
      </div>
    )
  }

  if (!data) {
    return null
  }

  const monthLabel = format(new Date(data.asOf), 'MMM')
  const numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'en')

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
      <StatCard
        label="Total Employees"
        value={numberFormat.format(data.totalEmployees)}
        icon={Users}
        tint="purple"
        delta={{ value: `${numberFormat.format(data.joinedThisMonth)} this month`, direction: 'up' }}
      />
      <StatCard
        label="Present Today"
        value={numberFormat.format(data.presentToday)}
        icon={UserCheck}
        tint="green"
        caption={`${data.presentPercent.toFixed(1)}% of total`}
      />
      <StatCard
        label="On Leave Today"
        value={numberFormat.format(data.onLeaveToday)}
        icon={CalendarDays}
        tint="orange"
        caption={`${data.onLeavePercent.toFixed(1)}% of total`}
      />
      <StatCard
        label="Absent Today"
        value={numberFormat.format(data.absentToday)}
        icon={UserX}
        tint="red"
        caption={`${data.absentPercent.toFixed(1)}% of total`}
      />
      <StatCard
        label={`New Joiners (${monthLabel})`}
        value={numberFormat.format(data.newJoinersThisMonth)}
        icon={UserPlus}
        tint="blue"
        delta={
          data.newJoinersChangePercent === null
            ? undefined
            : {
                value: `${Math.abs(data.newJoinersChangePercent).toFixed(0)}% from last month`,
                direction: data.newJoinersChangePercent >= 0 ? 'up' : 'down',
              }
        }
        // No baseline last month means no percentage to show — a dash beats "Infinity%".
        caption={data.newJoinersChangePercent === null ? 'No comparison available' : undefined}
      />
    </div>
  )
}
